// Composing delegate — the headless hook moved to `@heidi/tasks/data`
// (OpenSpec `monorepo-tasks-data-core` · D2, ARCFE-945), resolving cohort C
// per the ratified P0.5 option 1: THIS file is where the app composes
// Session-cache + Capture-offline state and hands the package hook the
// `isOfflineSession` fact as a parameter. The package stays ignorant of both
// domains; the closure stays `[@heidi/tasks]`; call sites keep this path and
// signature unchanged.
//
// It also owns the app reaction (ARCFE-921): the fetch-error toast crosses the
// `onFetchError` port with byte-identical copy, per-attempt timing, and the
// legacy AxiosError-only gating (enforced package-side).
import { toast } from '@heidi/design-system/toast';
import { useQueryClinicalTasksBySessionId as useQueryClinicalTasksBySessionIdCore } from '@heidi/tasks/data';
import { useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';

import {
  offlineLocalSessionsAtom,
  offlineSyncQueueAtom,
} from '@/atoms/offline-recording';
import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import { isOfflineLocalSession } from '@/utils/offline-recording/localSessionSettings';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';

// Definition moved to `@heidi/tasks/contracts` (D2); import + re-export because
// consumers key caches off it through this path.
import { sanitizeError } from '@heidi/runtime/logger';
import { GET_CLINICAL_TASKS_BY_SESSION_ID } from '@heidi/tasks/contracts';

export { GET_CLINICAL_TASKS_BY_SESSION_ID };

interface UseQueryClinicalTasksBySessionIdDelegateOptions {
  /** Caller-side gate forwarded to the package hook; defaults to `true`. */
  enabled?: boolean;
}

export const useQueryClinicalTasksBySessionId = (
  sessionId: string,
  { enabled = true }: UseQueryClinicalTasksBySessionIdDelegateOptions = {}
) => {
  const intl = useIntl();
  const offlineSyncQueue = useAtomValue(offlineSyncQueueAtom);
  const offlineLocalSessions = useAtomValue(offlineLocalSessionsAtom);

  const sessionData = reactQueryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);
  const isOfflineSession =
    isOfflineLocalSession(sessionData) ||
    offlineLocalSessions.some((session) => session.localSessionId === sessionId) ||
    offlineSyncQueue.some(
      (item) => item.localSessionId === sessionId || item.serverSessionId === sessionId
    );

  return useQueryClinicalTasksBySessionIdCore(sessionId, {
    isOfflineSession,
    enabled,
    onFetchError: (error) => {
      // The package logs `error.response?.data`, which is `undefined` on a raw
      // transport failure (`ERR_NETWORK`). Project through `sanitizeError` so a
      // suppressed toast still leaves a status/code diagnostic in Datadog — and
      // so the raw axios object (URL, Authorization, response body) never does.
      logger.error('Error fetching tasks', sanitizeError(error));
      if (shouldSuppressDesktopOfflineNetworkErrorToast(error)) return;

      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          id: 'vbY2EII0Bh',
          defaultMessage: 'Error fetching tasks',
        }),
        description: intl.formatMessage({
          id: 'QBKjO4Xx3+',
          defaultMessage: 'Could not load tasks for this session.',
        }),
      });
    },
  });
};
